import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../../../context/AuthContext";
import { API_URL } from "./constants";
import "./CopyWeekTab.css";

// Admin-only weekly planning tab: show everything that ran in the last
// 7 days, tick the ones to repeat, optionally tweak title / time /
// location per event, and copy them all to the SAME WEEKDAY next week
// in one click.
//
// Creation goes through the same socialevents POST the Create Event
// page uses — the backend generates the new socialEventId from the new
// date + title and stamps createdBy from the admin's token. The poll
// field is deliberately NOT copied: last week's GroupMe poll belongs
// to last week's event.
//
// Fields the source listing doesn't expose (hosts, password) fall back
// to defaults — set hosts on the copies via Edit Events if they matter.

const SOCIALEVENTS_POST_URL =
  "https://4cljs7mcdi.execute-api.us-east-1.amazonaws.com/default/socialevents";

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

// socialEventId always starts YYYY-MM-DD — the most reliable date
// source on the row (the `date` attribute isn't in the list
// projection).
const dateFromId = (socialEventId) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(socialEventId || "");
  if (!m) return null;
  return new Date(
    parseInt(m[1], 10),
    parseInt(m[2], 10) - 1,
    parseInt(m[3], 10)
  );
};

const addDays = (date, days) => {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
};

const shortLabel = (date) =>
  `${WEEKDAYS[date.getDay()]} ${date.getMonth() + 1}/${date.getDate()}`;

// "Month D, YYYY" — the format the socialevents POST schema expects
// (matches CreateEvent.js).
const longDate = (date) =>
  `${MONTHS[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;

const CopyWeekTab = () => {
  const { getAuthHeaders } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  // Per-event working state keyed by socialEventId:
  // { selected, title, time, privateLocation, status, message }
  const [drafts, setDrafts] = useState({});
  const [copying, setCopying] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const resp = await fetch(
        `${API_URL}?type=socialEvents&days=1&pastDays=7`,
        { method: "GET", headers: getAuthHeaders() }
      );
      const data = await resp.json();
      if (!resp.ok) {
        throw new Error(data?.error || data?.message || `HTTP ${resp.status}`);
      }
      // Past events only (drop today's upcoming leakage from days=1),
      // oldest first so the list reads Mon -> Sun like a planner.
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const list = (data.upcomingEvents || [])
        .filter((ev) => {
          const d = dateFromId(ev.socialEventId);
          return d && d < today;
        })
        .sort((a, b) =>
          (a.socialEventId || "").localeCompare(b.socialEventId || "")
        );
      setEvents(list);
      setDrafts((prev) => {
        const next = {};
        for (const ev of list) {
          next[ev.socialEventId] = prev[ev.socialEventId] || {
            selected: false,
            title: ev.title || "",
            time: ev.time || "",
            privateLocation: ev.privateLocation || "",
            status: "idle",
            message: "",
          };
        }
        return next;
      });
    } catch (err) {
      setError(err.message || "Failed to load last week's events");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    load();
  }, [load]);

  const setDraft = (id, patch) => {
    setDrafts((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));
  };

  const selectedIds = useMemo(
    () => events.map((e) => e.socialEventId).filter((id) => drafts[id]?.selected),
    [events, drafts]
  );

  const setAll = (selected) => {
    setDrafts((prev) => {
      const next = { ...prev };
      for (const ev of events) {
        next[ev.socialEventId] = { ...next[ev.socialEventId], selected };
      }
      return next;
    });
  };

  // Build the POST payload from the source row + the admin's edits.
  // Only keys in the socialevents Joi schema — unknown keys are
  // rejected. poll intentionally reset; hosts/password aren't in the
  // list projection so they fall back to safe defaults.
  const buildPayload = (ev, draft, targetDate) => ({
    title: (draft.title || ev.title || "").trim() || "Untitled event",
    password: "admin",
    description: ev.description || draft.title || ev.title || "Event",
    date: longDate(targetDate),
    time: (draft.time || ev.time || "6:00 PM").trim(),
    length: ev.length != null && ev.length !== "" ? String(ev.length) : "2 hrs",
    location: ev.location || "join group for location",
    generalpublic: ev.generalpublic !== false,
    privateLocation:
      (draft.privateLocation || ev.privateLocation || ev.location || "").trim() ||
      "join group for location",
    ...(ev.imgKey ? { imgKey: ev.imgKey } : {}),
    chat: ev.chat || "",
    groupMeChatId: ev.groupMeChatId || "",
    groupmeImg: ev.groupmeImg || "",
    groupMeChatUrl: ev.groupMeChatUrl || "",
    pollPrivateLocation: ev.pollPrivateLocation || "",
    pollText: ev.pollText || "",
    poll: "",
  });

  const handleCopy = async () => {
    if (selectedIds.length === 0) return;
    if (
      !window.confirm(
        `Create ${selectedIds.length} event${
          selectedIds.length === 1 ? "" : "s"
        } for next week (same weekdays)?`
      )
    ) {
      return;
    }
    setCopying(true);
    const headers = { "Content-Type": "application/json" };
    const token =
      typeof window !== "undefined" &&
      window.sessionStorage &&
      window.sessionStorage.getItem("cognitoIdToken");
    if (token) headers.Authorization = `Bearer ${token}`;

    for (const id of selectedIds) {
      const ev = events.find((e) => e.socialEventId === id);
      const draft = drafts[id];
      if (!ev || !draft) continue;
      const sourceDate = dateFromId(id);
      const targetDate = addDays(sourceDate, 7);
      setDraft(id, { status: "copying", message: "" });
      try {
        const resp = await fetch(SOCIALEVENTS_POST_URL, {
          method: "POST",
          headers,
          body: JSON.stringify(buildPayload(ev, draft, targetDate)),
        });
        if (!resp.ok) {
          let msg = `HTTP ${resp.status}`;
          try {
            const out = await resp.json();
            msg = out?.error || out?.message || msg;
          } catch (e) {
            /* keep default */
          }
          throw new Error(msg);
        }
        setDraft(id, {
          status: "done",
          message: `Created for ${shortLabel(targetDate)}`,
          selected: false,
        });
      } catch (err) {
        setDraft(id, {
          status: "error",
          message: err.message || "Copy failed",
        });
      }
    }
    setCopying(false);
  };

  return (
    <div className="copyweek-tab">
      <div className="copyweek-tab__header">
        <h2 className="copyweek-tab__title">Copy Week Forward</h2>
        <button
          type="button"
          className="copyweek-tab__btn"
          onClick={load}
          disabled={loading || copying}
        >
          {loading ? "Loading…" : "Refresh"}
        </button>
      </div>
      <p className="copyweek-tab__hint">
        Last 7 days of events. Tick the ones to repeat, adjust title / time /
        location where needed, then copy them to the <strong>same weekday
        next week</strong>. Copies don't carry last week's GroupMe poll or
        hosts — attach those via Edit Events afterwards if needed.
      </p>

      {error && <div className="copyweek-tab__error">Error: {error}</div>}

      {!loading && !error && events.length === 0 && (
        <div className="copyweek-tab__empty">
          No events found in the last 7 days.
        </div>
      )}

      {events.length > 0 && (
        <>
          <div className="copyweek-tab__toolbar">
            <button
              type="button"
              className="copyweek-tab__btn"
              onClick={() => setAll(true)}
              disabled={copying}
            >
              Select all
            </button>
            <button
              type="button"
              className="copyweek-tab__btn"
              onClick={() => setAll(false)}
              disabled={copying}
            >
              Clear
            </button>
            <span className="copyweek-tab__count">
              {selectedIds.length} selected
            </span>
            <button
              type="button"
              className="copyweek-tab__btn copyweek-tab__btn--primary"
              onClick={handleCopy}
              disabled={copying || selectedIds.length === 0}
            >
              {copying
                ? "Copying…"
                : `Copy ${selectedIds.length || ""} to next week`}
            </button>
          </div>

          <div className="copyweek-tab__list">
            {events.map((ev) => {
              const id = ev.socialEventId;
              const draft = drafts[id] || {};
              const sourceDate = dateFromId(id);
              const targetDate = sourceDate ? addDays(sourceDate, 7) : null;
              return (
                <div
                  key={id}
                  className={
                    "copyweek-tab__row" +
                    (draft.selected ? " copyweek-tab__row--selected" : "") +
                    (draft.status === "done" ? " copyweek-tab__row--done" : "")
                  }
                >
                  <label className="copyweek-tab__row-check">
                    <input
                      type="checkbox"
                      checked={!!draft.selected}
                      disabled={copying || draft.status === "copying"}
                      onChange={(e) =>
                        setDraft(id, { selected: e.target.checked })
                      }
                    />
                    <span className="copyweek-tab__dates">
                      {sourceDate ? shortLabel(sourceDate) : "?"}
                      <span className="copyweek-tab__arrow">→</span>
                      {targetDate ? shortLabel(targetDate) : "?"}
                    </span>
                  </label>

                  <div className="copyweek-tab__row-fields">
                    <div className="copyweek-tab__source-title">
                      {ev.title || id}
                    </div>
                    {draft.selected && (
                      <div className="copyweek-tab__edits">
                        <label>
                          Title
                          <input
                            type="text"
                            value={draft.title}
                            onChange={(e) =>
                              setDraft(id, { title: e.target.value })
                            }
                            disabled={copying}
                          />
                        </label>
                        <label>
                          Time
                          <input
                            type="text"
                            value={draft.time}
                            placeholder="6:00 PM"
                            onChange={(e) =>
                              setDraft(id, { time: e.target.value })
                            }
                            disabled={copying}
                          />
                        </label>
                        <label>
                          Private location
                          <input
                            type="text"
                            value={draft.privateLocation}
                            placeholder="Venue, address"
                            onChange={(e) =>
                              setDraft(id, { privateLocation: e.target.value })
                            }
                            disabled={copying}
                          />
                        </label>
                      </div>
                    )}
                    {draft.status === "done" && (
                      <div className="copyweek-tab__status copyweek-tab__status--done">
                        ✓ {draft.message}
                      </div>
                    )}
                    {draft.status === "error" && (
                      <div className="copyweek-tab__status copyweek-tab__status--error">
                        {draft.message}
                        <button
                          type="button"
                          className="copyweek-tab__btn"
                          style={{ marginLeft: 8 }}
                          onClick={() => setDraft(id, { status: "idle" })}
                        >
                          Dismiss
                        </button>
                      </div>
                    )}
                    {draft.status === "copying" && (
                      <div className="copyweek-tab__status">Creating…</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default CopyWeekTab;
